import { LitElement, html } from "../vendor/lit-element/lit-element";
import { STATES } from "./cbDetailStates";
import "./Disease";
import "./DiseaseGlass";
import "./Bone";
import "./BoneGlass";
import "./Mortality";
import "./MortalityGlass";

class ClinicalBurdensDetail extends LitElement {
  static get properties() {
    return {
      activeState: { type: Number },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    const wheel = document.querySelector("clinical-burdens");
    const boundHandler = this._handleStateChanged.bind(this);
    wheel.addEventListener("detail-changed", boundHandler);
  }

  _handleStateChanged(event) {
    this.activeState = event.detail.state;
  }

  constructor() {
    super();

    this.activeState = STATES.diseaseGlass;
    this.stateData = [
      {
        heading: html`
          <h3>
            Elevated phosphate levels lead to increased risk of mortality<sup
              >13,&sect;</sup
            >
          </h3>
        `,
        inner: html`<disease-state></disease-state>`,
        footnotes: html`<p class="footnote">
            <sup>&sect;</sup>Real-world multinational study with 17,414 HD
            patients in phase 4-6 (DOPPS 2009-2018). Analyzed per Model 5. Model
            5 analysis was stratified by country and phase, while adjusting for
            age, sex, dialysis vintage, mean 6-month albumin and phosphorus <2.8
            mg/dL, history of comorbidities (diabetes, hypertension, congestive
            heart failure, peripheral vascular disease, cancer, coronary artery
            disease, other CV disease, cerebrovascular disease, gastrointestinal
            bleeding, lung disease), and the most recent value of PTH prior to
            start of follow-up.
          </p>
          <p class="footnote">
            <sup>&Vert;</sup>Mean monthly phosphorus AUC represents the extent
            to which serum phosphorus was above 4.5 mg/dL on average each month
            during a 6-month period.
          </p>
          <p class="footnote">
            AUC=area under the curve; CHF=congestive heart failure;
            HD=hemodialysis; MACE=major adverse cardiovascular events (CV death
            + non-fatal myocardial infarction rates + non-fatal angina +
            non-fatal stroke); PTH=parathyroid hormone.
          </p> `,
      },
      {
        heading: html`<h3>CV disease risks include<sup>10,11</sup>:</h3>`,
        inner: html`<disease-glass-state
          style="opacity: 0;"
        ></disease-glass-state>`,
        footnotes: html`
          <p class="footnote">
            <sup>&sect;</sup> An analysis of 3490 patients with NDD-CKD (Stage 3
            or 4).
          </p>
          <p class="footnote">
            <sup>&Vert;</sup>After adjusting for traditional cardiovascular risk
            factors and estimated kidney function.
          </p>
          <p class="footnote">NDD=non-dialysis dependent.</p>
        `,
      },
      {
        heading: html`<h3>
          Odds ratio of hip fractures<sup>16,&sect;</sup>:
        </h3>`,
        inner: html`<bone-state style="opacity: 0;"></bone-state>`,
        footnotes: html`<p class="footnote">
            <sup>&sect;</sup>GFR of 60-80 and 80-100 mL/min did not show
            significant difference compared to GFR >100 mL/min.
          </p>
          <p class="footnote">GFR=glomerular filtration rate.</p>`,
      },
      {
        heading: html`<h3>Bone disease risks include<sup>6,10,15</sup>:</h3>`,
        inner: html`<bone-glass-state style="opacity: 0;"></bone-glass-state>`,
        footnotes: html`<p class="footnote"></p>`,
      },
      {
        heading: html`<h3>
          All-cause mortality rate increased as serum phosphate levels
          increased<sup>11,&sect;</sup>
        </h3>`,
        inner: html`<mortality-state style="opacity: 0;"></mortality-state>`,
        footnotes: html`<p class="footnote">
            <sup>&sect;</sup>In an observational study of 3490 patients with
            NDD-CKD (Stage 3 or 4), that analyzed the associations between
            elevated serum phosphate levels and the risk for mortality and MI
            among patients with CKD, independent of renal function and other
            known confounding factors.
          </p>
          <p class="footnote">
            MI=myocardial infarction; NDD=non-dialysis dependent.
          </p>`,
      },
      {
        heading: html`<h3>High FGF23<sup>14,&sect;</sup></h3>`,
        inner: html`<mortality-glass-state
          style="opacity: 0;"
        ></mortality-glass-state>`,
        footnotes: html`<p class="footnote">
            <sup>&sect;</sup>In a study of 219 patients undergoing hemodialysis,
            the 2-year mortality rate was significantly higher for hemodialysis
            patients with serum FGF23 levels in the highest quartile than in the
            lowest quartile [<i>P</i>=0.007; hazard ratio, 2.5 (1.3–5)].
          </p>
          <p class="footnote">FGF23=fibroblast growth factor 23.</p>`,
      },
    ];
  }

  createRenderRoot() {
    return this;
  }

  render() {
    return html`
      <div class="clinical-burdens-detail-wrapper">
        ${this.stateData[this.activeState].heading}
        <div class="clinical-burdens-detail-inner">
          ${this.stateData[this.activeState].inner}
        </div>
      </div>
      ${this.stateData[this.activeState].footnotes}
    `;
  }
}

customElements.define("clinical-burdens-detail", ClinicalBurdensDetail);
